import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Timesheet, TimesheetDay } from 'api/report/types';
import { TimesheetReportTemplate } from 'utils/report';
import { TimesheetSettingsType } from 'api/customer/types';
import { TravelsheetKeyType } from 'report/logic/timesheet/calculations';
import TravelsheetComponent from './Travelsheet';
import BottomNavigation from 'components/interface/bottomNavigation/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import CarIcon from '@mui/icons-material/DirectionsCarOutlined';
import { IconButton } from 'components/IconButton';
import Typography from '@mui/material/Typography';
import { WEEKDAY } from '../utils';
import { Skeleton } from '@mui/material';
import StartStopTimesheet from './StartStopTimesheet';
import NormalTimesheet from './NormalTimesheet';
import { ProfessionIdentifier } from 'api/supplier/types';
import Alert, { ALERT_STATUS } from 'components/interface/alert/Alert';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import NotFound from 'components/interface/NotFound/NotFound';
import TextArea from 'components/interface/textField/TextArea';
import { BinarySwitch } from 'components/interface/Switch';
import { LoadingSpinner } from 'components/interface/LoadingSpinner';
import { usePageScroll } from 'components/PageScrollContext';

const useStyles = makeStyles()(theme => ({
    spinner: {
        marginTop: theme.spacing(2),
    },
    selector: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
    },
    dateTextWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    enableSickHours: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: theme.spacing(1),
    },
    footer: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
}));

export interface TimesheetProps {
    timesheet: Timesheet | null;
    totalHours: string;
    template: TimesheetReportTemplate | null;
    timesheetSetting: TimesheetSettingsType;
    canSubmit: boolean;
    disabled: boolean;
    useSplitTextfields: boolean;
    allowTravelsheet: boolean;
    loading: boolean;
    error: boolean;
    enableSickHours: boolean;
    onEnableSickHours: (value: boolean) => void;
    onUpdateTimesheetDay: (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => void;
    onTravelsheetFieldChange: (
        key: TravelsheetKeyType,
        index: number
    ) => (value: string) => void;
    onNotesFieldChange: (value: string) => void;

    commentContainsSickHints: boolean;
    sickHoursDuplicateDays: TimesheetDay[];
}

const View = ({
    timesheet,
    totalHours,
    template,
    timesheetSetting,
    canSubmit,
    disabled,
    useSplitTextfields,
    allowTravelsheet,
    loading,
    error,
    enableSickHours,
    onEnableSickHours,
    onUpdateTimesheetDay,
    onTravelsheetFieldChange,
    onNotesFieldChange,

    commentContainsSickHints,
    sickHoursDuplicateDays,
}: TimesheetProps): JSX.Element => {
    const { classes } = useStyles();
    const { scrollTo } = usePageScroll();
    const [activePage, setActivePage] = React.useState<1 | 2>(1);
    const [selectedDay, setSelectedDay] = React.useState<number>(0);
    const isSSK = template?.professions.some(
        profession => profession.identifier === ProfessionIdentifier.NURSE
    );
    const allowSickHours = isSSK && canSubmit;

    if (timesheet === null || template === null) {
        return <></>;
    }

    const days = timesheet.days ?? [];

    const getNextDayIndex = (current: number): number => {
        for (let i = current + 1; i < 8; i++) {
            if (days[i]) {
                return i;
            }
        }
        return current;
    };

    const getPrevDayIndex = (current: number): number => {
        for (let i = current - 1; i >= 0; i--) {
            if (days[i]) {
                return i;
            }
        }
        return current;
    };

    const isNextArrowDisabled = () =>
        getNextDayIndex(selectedDay) === selectedDay;
    const isPrevArrowDisabled = () =>
        getPrevDayIndex(selectedDay) === selectedDay;

    const handleIncrementSelector = () =>
        setSelectedDay(prev => getNextDayIndex(prev));

    const handleDecrementSelector = () =>
        setSelectedDay(prev => getPrevDayIndex(prev));

    const handlePageChange = (_: any, newValue: number) => {
        if (newValue !== 1 && newValue !== 2) {
            return;
        }
        setActivePage(prev => {
            if (newValue !== prev) {
                scrollTo({ top: 0, left: 0 });
            }
            return newValue;
        });
    };

    const renderBottomNav = () => {
        return (
            <BottomNavigation value={activePage} onChange={handlePageChange}>
                <BottomNavigationAction
                    label='Tidrapport'
                    value={1}
                    icon={<AssignmentIcon />}
                />
                <BottomNavigationAction
                    label='Reseräkning'
                    value={2}
                    icon={<CarIcon />}
                />
            </BottomNavigation>
        );
    };

    const renderSelector = (): React.ReactNode => {
        const day = days[selectedDay];
        if (!day) {
            return null;
        }

        return (
            <div className={classes.selector}>
                <IconButton
                    disabled={isPrevArrowDisabled()}
                    onClick={handleDecrementSelector}
                    icon='arrow_back_ios_new'
                    variant='text'
                />
                <div className={classes.dateTextWrapper}>
                    <Typography
                        variant='h6'
                        color={
                            day?.holiday?.IsPublicHoliday
                                ? 'error'
                                : 'textPrimary'
                        }>
                        {`${WEEKDAY[day.weekday]} ${day.day}/${day.month}`}
                    </Typography>
                    {day?.holiday?.Name ? (
                        <Typography
                            variant='caption'
                            color={
                                day?.holiday?.IsPublicHoliday
                                    ? 'error'
                                    : 'textPrimary'
                            }>
                            <b>
                                {day?.holiday?.Name +
                                    (day?.holiday?.IsMajorHoliday
                                        ? ' (storhelg)'
                                        : '')}
                            </b>
                        </Typography>
                    ) : null}
                </div>

                <IconButton
                    disabled={isNextArrowDisabled()}
                    onClick={handleIncrementSelector}
                    icon='arrow_forward_ios'
                    variant='text'
                />
            </div>
        );
    };

    const renderTimesheet = () => {
        return (
            <>
                {renderSelector()}
                {allowSickHours && (
                    <div
                        className={classes.enableSickHours}
                        onClickCapture={event => event.preventDefault()}>
                        <BinarySwitch
                            label='Rapporera sjuktimmar'
                            value={enableSickHours || false}
                            onChange={v => onEnableSickHours(!!v)}
                            disabled={!canSubmit || disabled}
                            asToggleButton={false}
                        />
                    </div>
                )}
                {sickHoursDuplicateDays.length > 0 && (
                    <Alert
                        status={ALERT_STATUS.WARNING}
                        text={
                            <Typography variant='body2' color='inherit'>
                                Det verkar som att du har rapporterat sjuktimmar
                                som är lika med dina normala timmar. Om du har
                                varit sjuk behöver du inte rapportera
                                sjuktimmarna under arbetade timmar. Påverkade
                                dagar:
                                {sickHoursDuplicateDays.map((day, i) => (
                                    <i key={i}>
                                        <br />
                                        <b>
                                            {format(
                                                new Date(day.date),
                                                'EEEE',
                                                {
                                                    locale: sv,
                                                }
                                            )}
                                        </b>
                                    </i>
                                ))}
                            </Typography>
                        }
                        style={{ marginBottom: 16 }}
                        fullWidth
                    />
                )}
                {timesheetSetting === TimesheetSettingsType.START_STOP_TIME ? (
                    <StartStopTimesheet
                        day={days[selectedDay]}
                        dayIndex={selectedDay}
                        totalHours={totalHours}
                        canSubmit={canSubmit}
                        disabled={disabled}
                        loading={loading}
                        enableSickHours={enableSickHours}
                        onUpdateTimesheetDay={onUpdateTimesheetDay}
                    />
                ) : (
                    <NormalTimesheet
                        day={days[selectedDay]}
                        dayIndex={selectedDay}
                        template={template.data}
                        useSplitTextfields={useSplitTextfields}
                        canSubmit={canSubmit}
                        disabled={disabled}
                        loading={loading}
                        enableSickHours={enableSickHours}
                        onUpdateTimesheetDay={onUpdateTimesheetDay}
                    />
                )}
                <Typography
                    className={classes.footer}
                    variant='subtitle2'
                    {...(loading
                        ? {}
                        : {
                              dangerouslySetInnerHTML: {
                                  __html: template.data.footnote?.text || '',
                              },
                          })}>
                    {loading ? (
                        <Skeleton
                            animation='pulse'
                            variant='text'
                            width='300px'
                        />
                    ) : null}
                </Typography>
                {commentContainsSickHints &&
                    allowSickHours &&
                    !enableSickHours && (
                        <Alert
                            status={ALERT_STATUS.WARNING}
                            text='Det verkar som att du har skrivit något om sjukdom i din kommentar. Om du har varit sjuk under veckan, glöm inte att rapportera sjuktimmar. Det gör du genom att klicka på "Ja" vid "Rapportera sjuktimmar" högst upp på skärmen.'
                            style={{ marginBottom: 16 }}
                            fullWidth
                        />
                    )}
                <TextArea
                    label='Kommentar'
                    value={timesheet?.notes || ''}
                    onChange={onNotesFieldChange}
                    disabled={!canSubmit || disabled}
                    fullWidth
                    sx={{
                        marginBottom: 4,
                    }}
                />
            </>
        );
    };

    const renderTravelsheet = () => {
        if (allowTravelsheet && timesheet?.travelsheet) {
            return (
                <TravelsheetComponent
                    travelsheet={timesheet.travelsheet}
                    canSubmit={canSubmit}
                    disabled={disabled}
                    onTravelsheetFieldChange={onTravelsheetFieldChange}
                />
            );
        } else {
            return (
                <Typography variant='h5' color='textSecondary'>
                    Uppdraget har ej reseräkning
                </Typography>
            );
        }
    };

    const renderActivePage = () => {
        if (!allowTravelsheet) {
            return renderTimesheet();
        }
        switch (activePage) {
            case 1:
                return renderTimesheet();
            case 2:
                return renderTravelsheet();
        }
    };

    const renderPage = () => (
        <>
            {renderActivePage()}
            {allowTravelsheet && timesheet?.travelsheet
                ? renderBottomNav()
                : null}
        </>
    );

    return error ? (
        <NotFound variant='error' isMobile={false} />
    ) : (
        <LoadingSpinner loading={loading} className={classes.spinner} size={75}>
            {renderPage()}
        </LoadingSpinner>
    );
};

export default View;
